import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";

const DISMISS_KEY = "lara-sticky-cta-dismissed";

/**
 * Mobile-only sticky "Speak to us" bar. Renders a sentinel where it is mounted
 * (place it just after the hero); the bar appears once the sentinel has
 * scrolled above the viewport. Dismissal persists for the session.
 */
export function MobileStickyCta() {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      /* sessionStorage unavailable - leave enabled */
    }
  }, []);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
      {visible && !dismissed ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-brand-blue-dark pb-[env(safe-area-inset-bottom)] md:hidden">
          <div className="flex items-center gap-3 px-4 py-3">
            <p className="flex-1 text-sm font-semibold text-white">
              Ready to connect your systems?
            </p>
            <Link
              to="/contact"
              className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand-blue-dark"
            >
              Speak to us
            </Link>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/80 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-turquoise"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
